import { getAPIURL } from "./config";
import { aqFetch } from "./api";

/** Извлечь массив из ответа person_hours API (массив или обёртка { items/data/results/blocks }) */
const toPersonHoursArray = (raw) => {
  if (Array.isArray(raw)) return raw;
  if (raw && Array.isArray(raw.items)) return raw.items;
  if (raw && Array.isArray(raw.data)) return raw.data;
  if (raw && Array.isArray(raw.results)) return raw.results;
  if (raw && Array.isArray(raw.blocks)) return raw.blocks;
  return [];
};

export const addProject = async (projectData) => {
  try {
    const response = await fetch(`${getAPIURL()}projects/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(projectData),
      credentials: "include", // Добавьте эту строку, если используете куки для аутентификации
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка сервера: ${response.status} ${response.statusText}`,
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при создании проекта:", error);
    if (error instanceof TypeError && error.message === "Failed to fetch") {
      throw new Error(
        "Не удалось соединиться с сервером. Проверьте подключение к интернету и доступность сервера.",
      );
    }
    throw error;
  }
};

export const getProjectTypes = async () => {
  try {
    const response = await fetch(`${getAPIURL()}projects_types/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(`Ошибка при получении типов проектов: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении типов проектов:", error);
    throw error;
  }
};

export const getAllProjects = async () => {
  try {
    const response = await fetch(`${getAPIURL()}projects/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Сетевая ошибка при получении проектов");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении проектов:", error);
    throw error;
  }
};

export const getProjectPortfolio = async (retryCount = 0) => {
  const MAX_RETRIES = 3;
  const RETRY_DELAY = 1000; // 1 секунда базовая задержка

  try {
    const response = await fetch(
      `${getAPIURL()}projects/all/project_portfolio`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          // Отключаем компрессию для избежания проблем с Content-Length
          "Accept-Encoding": "identity",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(
        `Сетевая ошибка при получении портфолио проектов: ${response.status} ${response.statusText}`,
      );
    }

    // Проверяем Content-Length перед чтением ответа
    const contentLength = response.headers.get("content-length");
    let data;

    try {
      const responseText = await response.text();

      // Проверяем, что размер полученных данных соответствует Content-Length
      if (contentLength && parseInt(contentLength) !== responseText.length) {
        console.warn(
          `Несоответствие размера контента: ожидалось ${contentLength}, получено ${responseText.length}`,
        );
        // Если это не критично, продолжаем обработку
      }

      // Пытаемся распарсить JSON
      if (!responseText || responseText.trim() === "") {
        throw new Error("Пустой ответ от сервера");
      }

      data = JSON.parse(responseText);
    } catch (parseError) {
      // Если ошибка парсинга и есть попытки, повторяем запрос
      if (retryCount < MAX_RETRIES) {
        console.warn(
          `Ошибка парсинга ответа (попытка ${
            retryCount + 1
          }/${MAX_RETRIES}), повтор через ${RETRY_DELAY * (retryCount + 1)}мс:`,
          parseError,
        );
        await new Promise((resolve) =>
          setTimeout(resolve, RETRY_DELAY * (retryCount + 1)),
        );
        return getProjectPortfolio(retryCount + 1);
      }
      throw new Error(
        `Ошибка при обработке ответа сервера: ${parseError.message}`,
      );
    }

    // v2 эндпоинт может возвращать массив напрямую или объект с project_portfolio
    if (Array.isArray(data)) {
      return data;
    }
    return data.project_portfolio || data || [];
  } catch (error) {
    // Проверяем, является ли это ошибкой ERR_CONTENT_LENGTH_MISMATCH или Failed to fetch
    const isNetworkError =
      error.message.includes("Failed to fetch") ||
      error.message.includes("ERR_CONTENT_LENGTH_MISMATCH") ||
      error.message.includes("NetworkError") ||
      error.name === "TypeError";

    // Если это сетевая ошибка и есть попытки, повторяем запрос
    if (isNetworkError && retryCount < MAX_RETRIES) {
      const delay = RETRY_DELAY * Math.pow(2, retryCount); // Экспоненциальная задержка
      console.warn(
        `Сетевая ошибка при получении портфолио проектов (попытка ${
          retryCount + 1
        }/${MAX_RETRIES}), повтор через ${delay}мс:`,
        error.message,
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
      return getProjectPortfolio(retryCount + 1);
    }

    // Если все попытки исчерпаны или это не сетевая ошибка, пробрасываем ошибку
    console.error("Ошибка при получении портфолио проектов:", error);
    throw error;
  }
};

export const addProjectTeam = async (teamData) => {
  try {
    const response = await aqFetch(`projects/add_team`, {
      method: "POST",
      body: JSON.stringify(teamData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Ответ сервера:", response.status, response.statusText);
      console.error("Тело ответа:", errorData);
      throw new Error(
        errorData.detail || "Ошибка при добавлении команды проекта",
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при добавлении команды проекта:", error);
    throw error;
  }
};

export const updateProjectTeam = async (teamData) => {
  const { team_id, ...data } = teamData;
  try {
    const response = await aqFetch(`projects/teams/${team_id}`, {
      method: "PUT",
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error("Ответ сервера:", response.status, response.statusText);
      console.error("Тело ответа:", errorData);
      throw new Error(
        errorData.detail || "Ошибка при обновлении команды проекта",
      );
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при обновлении команды проекта:", error);
    throw error;
  }
};

export const getProjectById = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}projects/all?project_id=${projectId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(`Ошибка при получении проекта: ${errorText}`);
    }

    const data = await response.json();

    // Предполагаем, что API возвращает массив проектов, даже если запрашивается один проект
    if (Array.isArray(data) && data.length > 0) {
      return data[0]; // Возвращаем первый (и предположительно единственный) проект в массиве
    } else {
      throw new Error("Проект не найден");
    }
  } catch (error) {
    console.error("Ошибка при получении проекта:", error);
    throw error;
  }
};

export const getAllBlocks = async () => {
  try {
    const response = await fetch(`${getAPIURL()}blocks/all`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Сетевая ошибка при получении блоков");
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении блоков:", error);
    throw error;
  }
};

/**
 * Создать новый блок в справочнике (POST /blocks/add).
 * @param {string} name - Название блока
 * @param {boolean} [is_active=true] - Активность блока
 * @returns {Promise<string|number>} ID созданного блока
 */
export const addBlock = async (name, is_active = true) => {
  try {
    const response = await fetch(`${getAPIURL()}blocks/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({ name: String(name).trim(), is_active }),
    });

    if (!response.ok) {
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        const detail = errorData.detail;
        const msg = Array.isArray(detail)
          ? detail
              .map((d) => d.msg || d.message || JSON.stringify(d))
              .join("; ")
          : typeof detail === "string"
            ? detail
            : JSON.stringify(errorData);
        throw new Error(msg);
      }
      const text = await response.text();
      throw new Error(text || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return typeof data === "string" ? data : (data?.id ?? data);
  } catch (error) {
    console.error("Ошибка при создании блока:", error);
    throw error;
  }
};

export const updateProject = async (projectId, projectData) => {
  try {
    const response = await aqFetch(`projects/${projectId}`, {
      method: "PUT",
      body: JSON.stringify(projectData),
    });

    if (!response.ok) {
      // Сначала проверим, является ли ответ JSON
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        console.error("Ответ сервера:", response.status, response.statusText);
        console.error("Тело ответа:", errorData);
        throw new Error(errorData.detail || "Ошибка при обновлении проекта");
      } else {
        // Если ответ не JSON, получаем текст ошибки
        const errorText = await response.text();
        console.error("Ответ сервера:", response.status, response.statusText);
        console.error("Тело ответа:", errorText);
        throw new Error(`Ошибка сервера: ${response.status} ${errorText}`);
      }
    }

    const contentType = response.headers.get("content-type");
    if (contentType && contentType.includes("application/json")) {
      return await response.json();
    }
    return null; // или другое значение по умолчанию, если ответ не JSON
  } catch (error) {
    console.error("Ошибка при обновлении проекта:", error);
    throw error;
  }
};

export const getAllProjectsSummary = async () => {
  try {
    const response = await fetch(`${getAPIURL()}projects/all/summary`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(
        "Сетевая ошибка при получении краткой информации о проектах",
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при получении краткой информации о проектах:", error);
    throw error;
  }
};

/**
 * Получить все проекты Эскизного Проектирования
 * @returns {Promise<Array>} Массив проектов ЭП
 */
export const getSketchDesignProjects = async () => {
  const requestJson = async (url, errorMessage) => {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error(errorMessage);
    }

    return response.json();
  };

  const normalizeGeneralDesignContract = (item) => {
    const projectId = item?.project?.id ?? item?.project_id;
    const projectName = item?.project?.name;
    const contractSectionsId = item?.contract_sections_id;
    const sectionName = item?.section?.section?.name;
    if (!projectId || !projectName) return null;
    return {
      id: Number(projectId),
      name: String(projectName).trim(),
      source: "general_design",
      contract_sections_id:
        contractSectionsId != null ? Number(contractSectionsId) : null,
      section_name: sectionName ? String(sectionName).trim() : "",
    };
  };

  const normalizeSketchProject = (item) => {
    if (!item?.id || !item?.name) return null;
    return {
      ...item,
      id: Number(item.id),
      name: String(item.name).trim(),
      source: "sketch",
    };
  };

  const mergeProjects = (...projectLists) => {
    const unique = new Map();
    projectLists.flat().forEach((project) => {
      if (!project?.id || !project?.name) return;
      const normalized = {
        ...project,
        id: Number(project.id),
        name: String(project.name).trim(),
      };
      const isGeneral = normalized.source === "general_design";
      const uniqueKey = isGeneral
        ? `gd-${normalized.contract_sections_id ?? normalized.id}`
        : `sk-${normalized.id}`;
      const existing = unique.get(uniqueKey);
      // Если проект есть в обоих источниках, оставляем sketch, чтобы не ломать текущий поток.
      if (existing?.source === "sketch" && normalized.source !== "sketch") return;
      unique.set(uniqueKey, normalized);
    });
    return Array.from(unique.values());
  };

  try {
    const [sketchResult, generalDesignResult] = await Promise.allSettled([
      requestJson(
        `${getAPIURL()}sketch_design/tei/project/all`,
        "Сетевая ошибка при получении проектов Эскизного Проектирования",
      ),
      requestJson(
        `${getAPIURL()}general_design/all`,
        "Сетевая ошибка при получении проектов общего проектирования",
      ),
    ]);

    const sketchProjects =
      sketchResult.status === "fulfilled" && Array.isArray(sketchResult.value)
        ? sketchResult.value.map(normalizeSketchProject).filter(Boolean)
        : [];

    const generalDesignProjects =
      generalDesignResult.status === "fulfilled" &&
      Array.isArray(generalDesignResult.value)
        ? generalDesignResult.value
            .map(normalizeGeneralDesignContract)
            .filter(Boolean)
        : [];

    const mergedProjects = mergeProjects(sketchProjects, generalDesignProjects);

    if (
      mergedProjects.length === 0 &&
      sketchResult.status === "rejected" &&
      generalDesignResult.status === "rejected"
    ) {
      throw new Error(
        "Не удалось загрузить проекты Эскизного и общего проектирования",
      );
    }

    return mergedProjects;
  } catch (error) {
    console.error(
      "Ошибка при получении проектов Эскизного Проектирования:",
      error,
    );
    throw error;
  }
};

/**
 * Получить проекты ЭП, в которых текущий пользователь участвует в согласовании
 * GET /sketch_design/approvals/get/projects
 * @returns {Promise<Array>} Массив проектов ЭП с согласованиями текущего пользователя
 */
export const getSketchDesignProjectsWithApprovalsByUser = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/approvals/get/projects`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      throw new Error(
        "Сетевая ошибка при получении проектов ЭП с согласованиями пользователя",
      );
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Ошибка при получении проектов ЭП с согласованиями пользователя:",
      error,
    );
    throw error;
  }
};

/**
 * Получить информацию о конкретном проекте Эскизного Проектирования по ID
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Данные проекта ЭП
 */
export const getSketchDesignProjectById = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/tei/project/get/${projectId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Ошибка при получении проекта ЭП с ID ${projectId}:`, error);
    throw error;
  }
};

/**
 * Получить блоки проекта Эскизного Проектирования для ТЭП
 * GET /sketch_design/tei/blocks/get/{project_id}
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array<{id: number, name: string, area: number, rate: number, floor_coeff: number, project_id: number, spot_id: number, spot: object|null, order_index: number}>>} Массив блоков проекта (с order_index для порядка отображения)
 */
export const getSketchDesignBlocks = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/tei/blocks/get/${projectId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    const normalizeBlock = (block) => ({
      ...block,
      floor_coeff: block?.floor_coeff ?? block?.attachment_coeff ?? 0,
    });
    return Array.isArray(data) ? data.map(normalizeBlock) : [];
  } catch (error) {
    console.error(
      `Ошибка при получении блоков проекта ЭП с ID ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Получить все категории проекта Эскизного Проектирования
 * @returns {Promise<Array>} Массив категорий проекта с рекомендованной ставкой
 */
export const getSketchDesignProjectCategories = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/utils/project_categories/get`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Ошибка при получении категорий проекта ЭП:", error);
    throw error;
  }
};

/**
 * Добавить новую категорию проекта ЭП
 * @param {string} name - Название категории
 * @param {number} proposeRate - Рекомендованная ставка
 * @returns {Promise<Object>} Созданная категория
 */
export const addSketchDesignProjectCategory = async (name, proposeRate = 0) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/utils/project_categories/add`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          name: name.trim(),
          propose_rate: proposeRate,
        }),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при добавлении категории проекта ЭП:", error);
    throw error;
  }
};

/**
 * Получить список существующих названий блоков ЭП
 * @returns {Promise<Array>} Массив названий блоков
 */
export const getSketchDesignBlockNames = async () => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/utils/block_names/get`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Ошибка при получении названий блоков ЭП:", error);
    throw error;
  }
};

/**
 * Добавить новое название блока ЭП
 * @param {string} name - Название блока
 * @returns {Promise<Object>} Созданное название блока
 */
export const addSketchDesignBlockName = async (name) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/utils/block_names/add`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ name }),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при добавлении названия блока ЭП:", error);
    throw error;
  }
};

/**
 * Добавить блок в проект ЭП
 * @param {Object} blockData - Данные блока
 * @param {number} blockData.project_id - ID проекта
 * @param {string} blockData.name - Название блока
 * @param {boolean} blockData.is_active - Активен ли блок
 * @param {number} blockData.area - Площадь
 * @param {number} blockData.rate - Ставка
 * @param {number} blockData.floor_coeff - Коэффициент привязки (legacy-имя на фронте)
 * @param {number} [blockData.attachment_coeff] - Коэффициент привязки (имя бэкенда)
 * @param {number} [blockData.spot_id] - ID пятна (опционально)
 * @returns {Promise<Object>} Созданный блок
 */
export const addSketchDesignBlock = async (blockData) => {
  try {
    const payload = {
      ...blockData,
      attachment_coeff: blockData.attachment_coeff ?? blockData.floor_coeff ?? 0,
    };

    const response = await fetch(`${getAPIURL()}sketch_design/tei/blocks/add`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
      ...data,
      floor_coeff: data?.floor_coeff ?? data?.attachment_coeff ?? 0,
    };
  } catch (error) {
    console.error("Ошибка при добавлении блока ЭП:", error);
    throw error;
  }
};

/**
 * Обновить блок проекта ЭП
 * @param {number} blockId - ID блока
 * @param {Object} blockData - Данные блока для обновления
 * @param {number} blockData.area - Площадь
 * @param {number} blockData.rate - Ставка
 * @param {number} blockData.floor_coeff - Коэффициент привязки (legacy-имя на фронте)
 * @param {number} [blockData.attachment_coeff] - Коэффициент привязки (имя бэкенда)
 * @param {number|null} [blockData.spot_id] - ID пятна (опционально, null — отвязать от пятна)
 * @returns {Promise<Object>} Обновленный блок
 */
export const updateSketchDesignBlock = async (blockId, blockData) => {
  try {
    const attachmentCoeff =
      blockData.attachment_coeff ?? blockData.floor_coeff ?? 0;

    const updateData = {
      id: blockId,
      area: blockData.area ?? 0,
      rate: blockData.rate ?? 0,
      attachment_coeff: attachmentCoeff,
    };
    if (blockData.spot_id !== undefined) {
      updateData.spot_id = blockData.spot_id === null ? 0 : blockData.spot_id;
    }
    if (blockData.order_index !== undefined) {
      updateData.order_index = blockData.order_index;
    }

    const response = await fetch(`${getAPIURL()}sketch_design/tei/blocks/update`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify(updateData),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return {
      ...data,
      floor_coeff: data?.floor_coeff ?? data?.attachment_coeff ?? 0,
    };
  } catch (error) {
    console.error(`Ошибка при обновлении блока ЭП с ID ${blockId}:`, error);
    throw error;
  }
};

/**
 * Сохранить все блоки проекта ЭП
 * Обновляет каждый блок по отдельности, так как массового эндпоинта нет
 * @param {number} projectId - ID проекта
 * @param {Array} blocks - Массив блоков для сохранения
 * @returns {Promise<Object>} Результат сохранения
 */
export const saveSketchDesignBlocks = async (projectId, blocks) => {
  // Массового эндпоинта нет, поэтому обновляем блоки по одному
  let successCount = 0;
  let errorCount = 0;
  const errors = [];

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    try {
      const payload = {
        area: block.area ?? 0,
        rate: block.rate ?? 0,
        floor_coeff: block.floor_coeff ?? 0,
        order_index: i,
      };
      if (block.spot_id !== undefined) payload.spot_id = block.spot_id;
      await updateSketchDesignBlock(block.id, payload);
      successCount++;
    } catch (err) {
      console.error(`Ошибка при обновлении блока ${block.id}:`, err);
      errorCount++;
      errors.push({ blockId: block.id, error: err.message });
    }
  }

  if (errorCount > 0) {
    throw new Error(
      `Сохранено блоков: ${successCount}, ошибок: ${errorCount}. ${errors
        .map((e) => `Блок ${e.blockId}: ${e.error}`)
        .join("; ")}`,
    );
  }

  return {
    success: true,
    saved: successCount,
    total: blocks.length,
  };
};

/**
 * Удалить блок проекта ЭП
 * @param {number} blockId - ID блока
 * @returns {Promise<string>} Результат удаления
 */
export const deleteSketchDesignBlock = async (blockId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/tei/blocks/delete/${blockId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorText = await response.text();
        if (errorText) {
          try {
            const errorData = JSON.parse(errorText);
            errorMessage += ` - ${JSON.stringify(errorData)}`;
          } catch (e) {
            errorMessage += ` - ${errorText}`;
          }
        }
      } catch (e2) {
        // Игнорируем ошибку парсинга
      }
      throw new Error(errorMessage);
    }

    // Проверяем, есть ли контент в ответе
    const contentType = response.headers.get("content-type");
    const contentLength = response.headers.get("content-length");

    // Если контента нет или он пустой, считаем удаление успешным
    if (contentLength === "0" || !contentType) {
      return "Блок успешно удален";
    }

    // Пытаемся получить текст ответа
    try {
      const text = await response.text();
      if (!text || text.trim() === "") {
        return "Блок успешно удален";
      }

      // Пытаемся распарсить как JSON, если это JSON
      if (contentType.includes("application/json")) {
        try {
          return JSON.parse(text);
        } catch (e) {
          // Если не JSON, возвращаем текст
          return text;
        }
      }

      return text;
    } catch (e) {
      // Если не удалось прочитать ответ, считаем удаление успешным
      return "Блок успешно удален";
    }
  } catch (error) {
    console.error(`Ошибка при удалении блока ЭП с ID ${blockId}:`, error);
    throw error;
  }
};

/**
 * Получить список пятен проекта ЭП
 * GET /sketch_design/tei/spot/get/all/{project_id}
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array<{id: number, name: string, project_id: number}>>} Массив пятен
 */
export const getSketchDesignSpots = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/tei/spot/get/all/${projectId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (response.status === 404) {
      return [];
    }

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      `Ошибка при получении пятен проекта ЭП с ID ${projectId}:`,
      error,
    );
    throw error;
  }
};

/**
 * Создать пятно на проекте ЭП
 * @param {number} projectId - ID проекта
 * @param {Object} payload - { name: string }
 * @returns {Promise<{id: number, name: string, project_id: number}>}
 */
export const createSketchDesignSpot = async (projectId, payload) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/tei/spot/create/${projectId}`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ name: payload.name }),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при создании пятна ЭП:", error);
    throw error;
  }
};

/**
 * Обновить пятно ЭП
 * @param {number} spotId - ID пятна
 * @param {Object} payload - { name: string }
 * @returns {Promise<{id: number, name: string, project_id: number}>}
 */
export const updateSketchDesignSpot = async (spotId, payload) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/tei/spot/update/${spotId}`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ name: payload.name }),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error(`Ошибка при обновлении пятна ЭП с ID ${spotId}:`, error);
    throw error;
  }
};

/**
 * Удалить пятно ЭП
 * @param {number} spotId - ID пятна
 * @returns {Promise<string>}
 */
export const deleteSketchDesignSpot = async (spotId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}sketch_design/tei/spot/delete/${spotId}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {}
      }
      throw new Error(errorMessage);
    }

    const text = await response.text();
    return text || "Пятно удалено";
  } catch (error) {
    console.error(`Ошибка при удалении пятна ЭП с ID ${spotId}:`, error);
    throw error;
  }
};

/**
 * Обновить проект Эскизного Проектирования (категория и площадь участка)
 * @param {number} projectId - ID проекта
 * @param {string} sketchDesignCategory - Категория проекта
 * @param {number} fullPlaceArea - Площадь участка (га)
 * @returns {Promise<Object>} Обновленный проект
 */
export const updateSketchDesignProject = async (
  projectId,
  sketchDesignCategory,
  fullPlaceArea,
) => {
  try {
    const response = await fetch(`${getAPIURL()}sketch_design/tei/project/update`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
      body: JSON.stringify({
        project_id: Number(projectId),
        sketch_design_category: sketchDesignCategory || null,
        full_place_area: fullPlaceArea || 0,
      }),
    });

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`Ошибка при обновлении проекта ЭП с ID ${projectId}:`, error);
    throw error;
  }
};

/**
 * Получить отчёт по сотрудникам по проекту (трудозатраты)
 * @param {number} projectId - ID проекта
 * @param {Object} params - Параметры запроса
 * @param {string} [params.date_from] - Начало периода (дата отчёта)
 * @param {string} [params.date_to] - Конец периода (дата отчёта)
 * @param {number} [params.user_id] - Фильтр по сотруднику (user_id)
 */
export const getProjectPersonHours = async (projectId, params = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.append("date_from", params.date_from);
    if (params.date_to) searchParams.append("date_to", params.date_to);
    if (params.user_id != null) searchParams.append("user_id", params.user_id);
    const query = searchParams.toString();
    const url = `projects/${projectId}/person_hours${query ? `?${query}` : ""}`;
    const response = await aqFetch(url, {
      method: "GET",
    });

    if (!response.ok) {
      if (response.status === 404) {
        try {
          await response.json();
        } catch (e) {}
        return [];
      }
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.includes("application/json")) {
        const errorData = await response.json();
        throw new Error(
          errorData.detail ||
            "Ошибка при получении данных о трудозатратах проекта",
        );
      } else {
        const errorText = await response.text();
        throw new Error(`Ошибка сервера: ${response.status} ${errorText}`);
      }
    }

    const raw = await response.json();
    return toPersonHoursArray(raw);
  } catch (error) {
    console.error(
      "Ошибка при получении данных о трудозатратах проекта:",
      error,
    );
    throw error;
  }
};

/**
 * Отчёт по проекту по блокам
 * GET /projects/{project_id}/blocks/person_hours
 * @param {number} projectId
 * @param {Object} params - date_from?, date_to?
 */
export const getProjectBlocksPersonHours = async (projectId, params = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.append("date_from", params.date_from);
    if (params.date_to) searchParams.append("date_to", params.date_to);
    const query = searchParams.toString();
    const url = `projects/${projectId}/blocks/person_hours${query ? `?${query}` : ""}`;
    const response = await aqFetch(url, { method: "GET" });
    if (!response.ok) {
      if (response.status === 404) {
        try {
          await response.json();
        } catch (e) {}
        return [];
      }
      const errorData = await response.json();
      throw new Error(
        errorData.detail ||
          "Ошибка при получении данных о трудозатратах блоков",
      );
    }
    const raw = await response.json();
    return toPersonHoursArray(raw);
  } catch (error) {
    console.error("Ошибка при получении данных о трудозатратах блоков:", error);
    throw error;
  }
};

/**
 * Отчёт по проекту по разделам
 * GET /projects/{project_id}/sections/person_hours
 * @param {number} projectId
 * @param {Object} params - date_from?, date_to?
 */
export const getProjectSectionsPersonHours = async (projectId, params = {}) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.append("date_from", params.date_from);
    if (params.date_to) searchParams.append("date_to", params.date_to);
    const query = searchParams.toString();
    const url = `projects/${projectId}/sections/person_hours${query ? `?${query}` : ""}`;
    const response = await aqFetch(url, { method: "GET" });
    if (!response.ok) {
      if (response.status === 404) {
        try {
          await response.json();
        } catch (e) {}
        return [];
      }
      const errorData = await response.json();
      throw new Error(
        errorData.detail ||
          "Ошибка при получении данных о трудозатратах разделов",
      );
    }
    const raw = await response.json();
    return toPersonHoursArray(raw);
  } catch (error) {
    console.error(
      "Ошибка при получении данных о трудозатратах разделов:",
      error,
    );
    throw error;
  }
};

/**
 * Отчёт по проекту и блоку по разделам
 * GET /projects/{project_id}/blocks/{block_id}/sections/person_hours
 */
export const getBlockSectionsPersonHours = async (projectId, blockId) => {
  try {
    const response = await aqFetch(
      `projects/${projectId}/blocks/${blockId}/sections/person_hours`,
      { method: "GET" },
    );
    if (!response.ok) {
      if (response.status === 404) {
        try {
          await response.json();
        } catch (e) {}
        return [];
      }
      const errorData = await response.json();
      throw new Error(
        errorData.detail ||
          "Ошибка при получении данных о трудозатратах разделов блока",
      );
    }
    const raw = await response.json();
    return toPersonHoursArray(raw);
  } catch (error) {
    console.error(
      "Ошибка при получении данных о трудозатратах разделов блока:",
      error,
    );
    throw error;
  }
};

/**
 * Отчёт по проекту и разделу по блокам
 * GET /projects/{project_id}/sections/{section_id}/blocks/person_hours
 */
export const getSectionBlocksPersonHours = async (projectId, sectionId) => {
  try {
    const response = await aqFetch(
      `projects/${projectId}/sections/${sectionId}/blocks/person_hours`,
      { method: "GET" },
    );
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || "Ошибка при получении данных о блоках раздела",
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении данных о блоках раздела:", error);
    throw error;
  }
};

/**
 * Отчёт по блоку и разделу по сотрудникам
 * GET /projects/{project_id}/blocks/{block_id}/sections/{section_id}/person_hours
 * @param {number} projectId - ID проекта
 * @param {number} blockId - ID блока
 * @param {number} sectionId - ID раздела
 * @param {Object} params - Параметры запроса
 * @param {string} [params.date_from] - Начало периода
 * @param {string} [params.date_to] - Конец периода
 * @param {number} [params.user_id] - Фильтр по сотруднику
 */
export const getSectionPersonHours = async (
  projectId,
  blockId,
  sectionId,
  params = {},
) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.append("date_from", params.date_from);
    if (params.date_to) searchParams.append("date_to", params.date_to);
    if (params.user_id != null) searchParams.append("user_id", params.user_id);
    const query = searchParams.toString();
    const url = `projects/${projectId}/blocks/${blockId}/sections/${sectionId}/person_hours${query ? `?${query}` : ""}`;
    const response = await aqFetch(url, { method: "GET" });

    if (!response.ok) {
      if (response.status === 404) {
        try {
          await response.json();
        } catch (e) {}
        return [];
      }
      const errorData = await response.json();
      throw new Error(
        errorData.detail ||
          "Ошибка при получении данных о трудозатратах раздела",
      );
    }

    const raw = await response.json();
    return toPersonHoursArray(raw);
  } catch (error) {
    console.error(
      "Ошибка при получении данных о трудозатратах раздела:",
      error,
    );
    throw error;
  }
};

/**
 * Экспорт отчёта по блоку и разделу (сотрудники) в Excel
 * GET /projects/{project_id}/blocks/{block_id}/sections/{section_id}/person_hours/export
 */
export const exportSectionPersonHours = async (
  projectId,
  blockId,
  sectionId,
  options = {},
) => {
  const params = new URLSearchParams();
  if (options.date_from) params.append("date_from", options.date_from);
  if (options.date_to) params.append("date_to", options.date_to);
  if (options.user_id != null) params.append("user_id", options.user_id);
  const query = params.toString();
  const url = `${getAPIURL()}projects/${projectId}/blocks/${blockId}/sections/${sectionId}/person_hours/export${query ? `?${query}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || `Ошибка выгрузки: ${response.status}`);
  }
  const contentDisposition = response.headers.get("content-disposition");
  let filename = `Трудозатраты_блок_${blockId}_раздел_${sectionId}.xlsx`;
  if (contentDisposition) {
    const m = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (m && m[1]) {
      filename = m[1].replace(/['"]/g, "");
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {}
    }
  }
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Отчёт по разделу и блоку по сотрудникам
 * GET /projects/{project_id}/sections/{section_id}/blocks/{block_id}/person_hours
 */
export const getBlockPersonHours = async (
  projectId,
  sectionId,
  blockId,
  params = {},
) => {
  try {
    const searchParams = new URLSearchParams();
    if (params.date_from) searchParams.append("date_from", params.date_from);
    if (params.date_to) searchParams.append("date_to", params.date_to);
    if (params.user_id != null) searchParams.append("user_id", params.user_id);
    const query = searchParams.toString();
    const url = `projects/${projectId}/sections/${sectionId}/blocks/${blockId}/person_hours${query ? `?${query}` : ""}`;
    const response = await aqFetch(url, { method: "GET" });
    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        errorData.detail || "Ошибка при получении данных о трудозатратах блока",
      );
    }
    return await response.json();
  } catch (error) {
    console.error("Ошибка при получении данных о трудозатратах блока:", error);
    throw error;
  }
};

/**
 * Экспорт отчёта по разделу и блоку (сотрудники) в Excel
 * GET /projects/{project_id}/sections/{section_id}/blocks/{block_id}/person_hours/export
 */
export const exportBlockPersonHours = async (
  projectId,
  sectionId,
  blockId,
  options = {},
) => {
  const params = new URLSearchParams();
  if (options.date_from) params.append("date_from", options.date_from);
  if (options.date_to) params.append("date_to", options.date_to);
  if (options.user_id != null) params.append("user_id", options.user_id);
  const query = params.toString();
  const url = `${getAPIURL()}projects/${projectId}/sections/${sectionId}/blocks/${blockId}/person_hours/export${query ? `?${query}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || `Ошибка выгрузки: ${response.status}`);
  }
  const contentDisposition = response.headers.get("content-disposition");
  let filename = `Трудозатраты_раздел_${sectionId}_блок_${blockId}.xlsx`;
  if (contentDisposition) {
    const m = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (m && m[1]) {
      filename = m[1].replace(/['"]/g, "");
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {}
    }
  }
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Экспорт отчёта по проекту по блокам в Excel
 * GET /projects/{project_id}/blocks/person_hours/export
 */
export const exportProjectBlocksPersonHours = async (
  projectId,
  options = {},
) => {
  const params = new URLSearchParams();
  if (options.date_from) params.append("date_from", options.date_from);
  if (options.date_to) params.append("date_to", options.date_to);
  const query = params.toString();
  const url = `${getAPIURL()}projects/${projectId}/blocks/person_hours/export${query ? `?${query}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(
      (await response.text()) || `Ошибка выгрузки: ${response.status}`,
    );
  }
  const contentDisposition = response.headers.get("content-disposition");
  let filename = `Трудозатраты_проект_${projectId}_блоки.xlsx`;
  if (contentDisposition) {
    const m = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (m && m[1]) {
      filename = m[1].replace(/['"]/g, "");
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {}
    }
  }
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Экспорт отчёта по проекту по разделам в Excel
 * GET /projects/{project_id}/sections/person_hours/export
 */
export const exportProjectSectionsPersonHours = async (
  projectId,
  options = {},
) => {
  const params = new URLSearchParams();
  if (options.date_from) params.append("date_from", options.date_from);
  if (options.date_to) params.append("date_to", options.date_to);
  const query = params.toString();
  const url = `${getAPIURL()}projects/${projectId}/sections/person_hours/export${query ? `?${query}` : ""}`;
  const response = await fetch(url, {
    method: "GET",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new Error(
      (await response.text()) || `Ошибка выгрузки: ${response.status}`,
    );
  }
  const contentDisposition = response.headers.get("content-disposition");
  let filename = `Трудозатраты_проект_${projectId}_разделы.xlsx`;
  if (contentDisposition) {
    const m = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (m && m[1]) {
      filename = m[1].replace(/['"]/g, "");
      try {
        filename = decodeURIComponent(filename);
      } catch (e) {}
    }
  }
  const blob = await response.blob();
  const downloadUrl = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(downloadUrl);
};

/**
 * Выгрузить отчёт по сотрудникам проекта в Excel
 * GET /projects/{project_id}/person_hours/export
 * @param {number} projectId - ID проекта
 * @param {Object} options - Параметры выгрузки
 * @param {string} [options.date_from] - Начало периода
 * @param {string} [options.date_to] - Конец периода
 * @param {number} [options.user_id] - Фильтр по сотруднику
 * @returns {Promise<void>}
 */
export const exportProjectPersonHours = async (projectId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.date_from) params.append("date_from", options.date_from);
    if (options.date_to) params.append("date_to", options.date_to);
    if (options.user_id != null) params.append("user_id", options.user_id);
    const query = params.toString();
    const url = `${getAPIURL()}projects/${projectId}/person_hours/export${query ? `?${query}` : ""}`;

    const response = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      let errorMessage = "";
      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          const errorData = await response.json();
          errorMessage =
            errorData.detail || errorData.message || errorData.error || "";
        } else {
          errorMessage = await response.text();
        }
      } catch (e) {
        errorMessage = "";
      }
      if (response.status === 504) {
        throw new Error("Превышено время ожидания сервера. Попробуйте позже.");
      }
      if (response.status === 500) {
        throw new Error(
          errorMessage
            ? `Внутренняя ошибка сервера: ${errorMessage}`
            : "Внутренняя ошибка сервера. Обратитесь к администратору.",
        );
      }
      throw new Error(
        errorMessage
          ? `Ошибка при выгрузке трудозатрат (${response.status}): ${errorMessage}`
          : `Ошибка при выгрузке трудозатрат: ${response.status}`,
      );
    }

    const contentDisposition = response.headers.get("content-disposition");
    let filename = `Трудозатраты_проекта_${projectId}.xlsx`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, "");
        try {
          filename = decodeURIComponent(filename);
        } catch (e) {}
      }
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error("Ошибка при выгрузке трудозатрат проекта:", error);
    throw error;
  }
};

// ========== ФИНАНСОВЫЙ ПЛАН API ==========

/**
 * Получить типы исполнителей для финансового плана
 * @returns {Promise<Array<{id: number, name: string}>>}
 */
export const getFinPlanPerformerTypes = async () => {
  try {
    const response = await fetch(`${getAPIURL()}fin_plan/performer_types`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP ${response.status}: ${errorText}`);
    }
    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Error fetching fin plan performer types:", error);
    throw error;
  }
};

/**
 * Получить все данные финансового плана для проекта
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Данные финансового плана
 */
export const getFinancialPlanData = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching financial plan data:", error);
    throw error;
  }
};

/**
 * Сохранить данные финансового плана
 * @param {number} projectId - ID проекта
 * @param {Object} data - Данные для сохранения
 * @returns {Promise<Object>} Результат сохранения
 */
export const saveFinancialPlanData = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/save`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error saving financial plan data:", error);
    throw error;
  }
};

/**
 * Отправить финансовый план на согласование
 * @param {number} projectId - ID проекта
 * @param {Object} approvalData - Данные для согласования
 * @returns {Promise<Object>} Результат отправки
 */
export const submitFinancialPlanForApproval = async (
  projectId,
  approvalData,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/submit_approval`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(approvalData),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error submitting financial plan for approval:", error);
    throw error;
  }
};

/**
 * Получить историю согласований финансового плана
 * Использует endpoint для получения всех согласований проекта
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив версий согласований
 */
export const getFinancialPlanApprovals = async (projectId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/approvals`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error("Ошибка при получении истории согласований:", error);
    throw error;
  }
};

/**
 * Отправить финансовый план на согласование
 * @param {number} projectId - ID проекта
 * @param {Array<number>} approverIds - Массив ID согласующих
 * @param {number} totalPrice - Общая сумма
 * @returns {Promise<Object>} Результат отправки с полями message и version
 */
export const requestFinancialPlanApproval = async (
  projectId,
  approverIds,
  totalPrice,
) => {
  try {
    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/request_for_approval`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          approver_ids: approverIds,
          total_price: totalPrice,
        }),
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(`Ошибка при отправке на согласование: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при отправке на согласование:", error);
    throw error;
  }
};

/**
 * Отменить отправку финансового плана на согласование
 * @param {number} projectId - ID проекта
 * @returns {Promise<Object>} Результат отмены с полями message и version
 */
export const cancelFinancialPlanApprovalRequest = async (projectId) => {
  try {
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/cancel_approval_request`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при отмене согласования: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при отмене согласования:", error);
    throw error;
  }
};

/**
 * Восстановить версию финансового плана
 * @param {number} projectId - ID проекта
 * @param {number|null} version - Версия для восстановления (null для создания новой копии текущей версии)
 * @returns {Promise<Object>} Результат восстановления версии с полями message и version
 */
export const restoreFinancialPlanVersion = async (
  projectId,
  version = null,
) => {
  try {
    // Формируем body: если version === null, не отправляем поле version
    const body = version !== null ? { version: version } : {};

    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/restore_version`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(body),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Ошибка при восстановлении версии:", error);
    throw error;
  }
};

/**
 * Изменить статус согласования финансового плана
 * Позволяет согласующему изменить статус только своего шага
 * @param {number} projectId - ID проекта
 * @param {number} approvalId - ID согласования
 * @param {string} status - Статус: "согласован" или "не согласован"
 * @returns {Promise<Object>} Результат изменения статуса с полями message и version
 */
export const changeFinancialPlanApprovalStatus = async (
  projectId,
  approvalId,
  status,
) => {
  try {
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/my_approvals/${approvalId}/${status}`;

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при изменении статуса согласования: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Ошибка при изменении статуса согласования:", error);
    throw error;
  }
};

/**
 * Получить статусы согласования финансового плана
 * @param {number} projectId - ID проекта
 * @returns {Promise<Array>} Массив согласований с статусами
 */
export const getFinancialPlanApprovalStatuses = async (projectId) => {
  try {
    const url = `${getAPIURL()}fin_plan/projects/${projectId}/approvals`;

    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "include",
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Ответ сервера: ${response.status} ${response.statusText}`);
      console.error(`Тело ответа: ${errorText}`);
      throw new Error(
        `Ошибка при получении статусов согласования финансового плана: ${response.status} ${response.statusText}`,
      );
    }

    const data = await response.json();
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(
      "Ошибка при получении статусов согласования финансового плана:",
      error,
    );
    throw error;
  }
};

/**
 * Рассчитать финансовый план
 * @param {number} projectId - ID проекта
 * @param {Object} data - Данные для расчета
 * @returns {Promise<Object>} Результат расчета
 */
export const calculateFinancialPlan = async (projectId, data) => {
  try {
    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/calculate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error("Error calculating financial plan:", error);
    throw error;
  }
};

/**
 * Получить детальные данные секции финансового плана
 * @param {number} projectId - ID проекта
 * @param {number} sectionId - ID секции
 * @returns {Promise<Object>} Детальные данные секции
 */
export const getFinancialPlanSectionDetails = async (projectId, sectionId) => {
  try {
    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/sections/${sectionId}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching financial plan section details:", error);
    throw error;
  }
};

/**
 * Формирует payload для калькуляции финансового плана
 * @param {number} projectId - ID проекта
 * @param {Object} financialPlanData - Данные финансового плана
 * @param {Object} sectionsData - Детальные данные секций
 * @returns {Promise<Object>} Payload для калькуляции
 */
export const buildFinancialPlanCalculationPayload = async (
  projectId,
  financialPlanData,
  sectionsData,
) => {
  // Получаем детали для всех секций
  const allSectionDetails = sectionsData || {};

  // Вспомогательная функция для безопасного получения числового значения
  const getNumericValue = (value, defaultValue = 0) => {
    if (typeof value === "number" && !isNaN(value)) {
      return value;
    }
    if (typeof value === "string" && !isNaN(parseFloat(value))) {
      return parseFloat(value);
    }
    return defaultValue;
  };

  // Вспомогательная функция для проверки, является ли раздел единым (без блоков)
  const isUnifiedSection = (sectionName) => {
    const name = sectionName?.toLowerCase().trim();
    return name === "инсоляция" || name === "гп";
  };

  // Разделяем разделы на обычные (с блоками) и единые (без блоков)
  const regularSections = (financialPlanData.construction_design || []).filter(
    (s) => !isUnifiedSection(s.section?.name),
  );
  const unifiedSections = (financialPlanData.construction_design || []).filter(
    (s) => isUnifiedSection(s.section?.name),
  );

  // Формируем construction_design только для обычных разделов (с блоками)
  const constructionDesign = regularSections.map((section) => {
    const sectionDetails = allSectionDetails[section.section.id];

    // Используем данные о блоках из sectionDetails
    const blocks = (sectionDetails?.block_expenditures || []).map((block) => {
      // Преобразуем в новую структуру с массивом performers
      const defaultPerformer = {
        employee: { id: 0, name: "" },
        performer_type: { id: 0, name: "" },
        entity_average_salary: 0,
        entity_name: "",
        day_efforts: 0,
      };
      let performers = [];
      if (
        block.block?.performers &&
        Array.isArray(block.block.performers) &&
        block.block.performers.length > 0
      ) {
        // Новая структура API с массивом performers
        performers = block.block.performers;
      } else if (block.block?.performer) {
        // Старая структура API с одним performer - преобразуем в массив
        performers = [block.block.performer];
      } else {
        // Дефолтный performer
        performers = [defaultPerformer];
      }

      return {
        tei_id: block.block?.tei_id || 0,
        day_efforts: block.day_efforts || 0,
        performers: performers,
      };
    });

    return {
      section_id: section.section.id,
      threshold_percent:
        sectionDetails?.threshold_percent ||
        section.expenditures_percent?.threshold ||
        0,
      blocks: blocks,
    };
  });

  // Формируем construction_design_project для единых разделов (без блоков)
  const constructionDesignProject = unifiedSections.map((section) => {
    const sectionDetails = allSectionDetails[section.section.id];
    const sectionName = section.section?.name?.toLowerCase().trim();
    const isInsolation = sectionName === "инсоляция";

    // Преобразуем performers из данных секции
    const defaultPerformer = {
      employee: { id: 0, name: "" },
      performer_type: { id: 0, name: "" },
      entity_average_salary: 0,
      entity_name: "",
      day_efforts: 0,
    };
    let performers = [];
    if (
      section.performers &&
      Array.isArray(section.performers) &&
      section.performers.length > 0
    ) {
      performers = section.performers;
    } else if (section.performer) {
      performers = [section.performer];
    } else {
      // Дефолтный performer
      performers = [defaultPerformer];
    }

    return {
      section_id: section.section.id,
      threshold_percent:
        sectionDetails?.threshold_percent ||
        section.expenditures_percent?.threshold ||
        0,
      // Для "Инсоляции" используем total_day_efforts, для "ГП" - 0 (рассчитывается на бэке)
      day_efforts: isInsolation ? sectionDetails?.total_day_efforts || 0 : 0,
      performers: performers,
    };
  });

  // Получаем глобальный процент бонусов
  const globalBonusPercent =
    Object.values(allSectionDetails || {})
      .map((det) => det.bonus_percent)
      .filter((val) => val !== undefined && val !== null)
      .find((val) => val > 0) ||
    getNumericValue(financialPlanData.bonus_percent_construction_design);

  // Формируем contract_sections из financialPlanData
  // Поддержка новой структуры: contract_sections.sections или старого массива
  let contractSectionsArray = [];
  if (financialPlanData.contract_sections) {
    if (
      financialPlanData.contract_sections.sections &&
      Array.isArray(financialPlanData.contract_sections.sections)
    ) {
      // Новая структура: объект с полем sections
      contractSectionsArray = financialPlanData.contract_sections.sections;
    } else if (Array.isArray(financialPlanData.contract_sections)) {
      // Старая структура: массив
      contractSectionsArray = financialPlanData.contract_sections;
    }
  }

  const contractSections = contractSectionsArray.map((cs) => ({
    section_id: cs.section?.id || cs.section_id,
    amount: cs.amount || 0,
  }));

  // Формируем блоки для калькуляции
  const payload = {
    bonus_percent_construction_design: globalBonusPercent,
    construction_design: constructionDesign,
    construction_design_project: constructionDesignProject, // Всегда включаем, даже если массив пустой
    bim_coordination: {
      include_bonuses:
        financialPlanData.bim_coordination?.include_bonuses ?? true,
      quality_control_percent:
        financialPlanData.bim_coordination?.quality_control_percent || 0,
      direct_bonus_percent:
        financialPlanData.bim_coordination?.direct_bonus_percent || 0,
      bim_role_salaries: (() => {
        // Группируем роли и преобразуем в новую структуру с массивом performers
        const roleMap = new Map();
        const defaultPerformer = {
          employee: { id: 0, name: "" },
          performer_type: { id: 0, name: "" },
          entity_average_salary: 0,
          entity_name: "",
          day_efforts: 0,
        };
        (financialPlanData.bim_coordination?.roles || []).forEach((role) => {
          if (!roleMap.has(role.role)) {
            roleMap.set(role.role, []);
          }
          // Преобразуем performer в массив performers
          if (
            role.performers &&
            Array.isArray(role.performers) &&
            role.performers.length > 0
          ) {
            role.performers.forEach((p) => roleMap.get(role.role).push(p));
          } else if (role.performer) {
            roleMap.get(role.role).push(role.performer);
          } else {
            roleMap.get(role.role).push(defaultPerformer);
          }
        });
        return Array.from(roleMap.entries()).map(([roleName, performers]) => ({
          role: roleName,
          // Если performers пустой, добавляем дефолтный performer
          performers: performers.length > 0 ? performers : [defaultPerformer],
        }));
      })(),
    },
    quality_control: {
      include_bonuses:
        financialPlanData.quality_control?.include_bonuses ?? true,
      quality_control_percent:
        financialPlanData.quality_control?.quality_control_percent || 0,
      direct_bonus_percent:
        financialPlanData.quality_control?.direct_bonus_percent || 0,
      normative_control_efforts:
        financialPlanData.quality_control?.normative_control_efforts || 0,
      quality_control_role_salaries: (() => {
        // Группируем роли и преобразуем в новую структуру с массивом performers
        const roleMap = new Map();
        const defaultPerformer = {
          employee: { id: 0, name: "" },
          performer_type: { id: 0, name: "" },
          entity_average_salary: 0,
          entity_name: "",
          day_efforts: 0,
        };
        (financialPlanData.quality_control?.roles || []).forEach((role) => {
          if (!roleMap.has(role.role)) {
            roleMap.set(role.role, []);
          }
          // Преобразуем performer в массив performers
          if (
            role.performers &&
            Array.isArray(role.performers) &&
            role.performers.length > 0
          ) {
            role.performers.forEach((p) => roleMap.get(role.role).push(p));
          } else if (role.performer) {
            roleMap.get(role.role).push(role.performer);
          } else {
            roleMap.get(role.role).push(defaultPerformer);
          }
        });
        return Array.from(roleMap.entries()).map(([roleName, performers]) => ({
          role: roleName,
          // Если performers пустой, добавляем дефолтный performer
          performers: performers.length > 0 ? performers : [defaultPerformer],
        }));
      })(),
    },
    project_delivery: {
      include_bonuses:
        financialPlanData.project_delivery?.include_bonuses ?? true,
      quality_control_percent:
        financialPlanData.project_delivery?.quality_control_percent || 0,
      direct_bonus_percent:
        financialPlanData.project_delivery?.direct_bonus_percent || 0,
      delivery_role_salaries: (() => {
        // Группируем роли и преобразуем в новую структуру с массивом performers
        const roleMap = new Map();
        const defaultPerformer = {
          employee: { id: 0, name: "" },
          performer_type: { id: 0, name: "" },
          entity_average_salary: 0,
          entity_name: "",
          day_efforts: 0,
        };
        (financialPlanData.project_delivery?.roles || []).forEach((role) => {
          if (!roleMap.has(role.role)) {
            roleMap.set(role.role, []);
          }
          // Преобразуем performer в массив performers
          if (
            role.performers &&
            Array.isArray(role.performers) &&
            role.performers.length > 0
          ) {
            role.performers.forEach((p) => roleMap.get(role.role).push(p));
          } else if (role.performer) {
            roleMap.get(role.role).push(role.performer);
          } else {
            roleMap.get(role.role).push(defaultPerformer);
          }
        });
        return Array.from(roleMap.entries()).map(([roleName, performers]) => ({
          role: roleName,
          // Если performers пустой, добавляем дефолтный performer
          performers: performers.length > 0 ? performers : [defaultPerformer],
        }));
      })(),
    },
    project_management: {
      include_bonuses:
        financialPlanData.project_management?.include_bonuses ?? true,
      quality_control_percent:
        financialPlanData.project_management?.quality_control_percent || 0,
      direct_bonus_percent:
        financialPlanData.project_management?.direct_bonus_percent || 0,
      administration_efforts:
        financialPlanData.project_management?.administration_efforts || 0,
      management_role_salaries: (() => {
        // Группируем роли и преобразуем в новую структуру с массивом performers
        const roleMap = new Map();
        const defaultPerformer = {
          employee: { id: 0, name: "" },
          performer_type: { id: 0, name: "" },
          entity_average_salary: 0,
          entity_name: "",
          day_efforts: 0,
        };
        (financialPlanData.project_management?.roles || []).forEach((role) => {
          if (!roleMap.has(role.role)) {
            roleMap.set(role.role, {
              performers: [],
              coefficient: role.coefficient || 0,
            });
          }
          const roleData = roleMap.get(role.role);
          roleData.coefficient = role.coefficient || roleData.coefficient || 0;
          // Преобразуем performer в массив performers
          if (
            role.performers &&
            Array.isArray(role.performers) &&
            role.performers.length > 0
          ) {
            role.performers.forEach((p) => roleData.performers.push(p));
          } else if (role.performer) {
            roleData.performers.push(role.performer);
          } else {
            roleData.performers.push(defaultPerformer);
          }
        });
        return Array.from(roleMap.entries()).map(([roleName, roleData]) => ({
          role: roleName,
          // Если performers пустой, добавляем дефолтный performer
          performers:
            roleData.performers.length > 0
              ? roleData.performers
              : [defaultPerformer],
          coefficient: roleData.coefficient,
        }));
      })(),
    },
  };

  // Добавляем contract_sections, если есть данные
  if (contractSections.length > 0) {
    payload.contract_sections = contractSections;
  }

  return payload;
};

/**
 * Рассчитать финансовый план с обновлением контекста
 * @param {number} projectId - ID проекта
 * @param {Object} financialPlanData - Данные финансового плана
 * @param {Object} sectionsData - Детальные данные секций
 * @returns {Promise<Object>} Результат расчета
 */
export const calculateFinancialPlanWithContext = async (
  projectId,
  financialPlanData,
  sectionsData,
) => {
  try {
    // Если данные не переданы, получаем их из API
    if (!financialPlanData) {
      const apiData = await getFinancialPlanData(projectId);
      financialPlanData = apiData;
    }

    // Формируем payload согласно новому API
    const payload = await buildFinancialPlanCalculationPayload(
      projectId,
      financialPlanData,
      sectionsData,
    );

    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/calculate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    // Возвращаем полный результат для лайв обновления интерфейса
    return result;
  } catch (error) {
    console.error("Error calculating financial plan:", error);
    throw error;
  }
};

/**
 * Рассчитать финансовый план для конкретной секции
 * @param {Object} contextData - Данные контекста
 * @param {number} projectId - ID проекта
 * @param {number} sectionId - ID секции
 * @param {Object} sectionData - Данные секции для обновления
 * @returns {Promise<Object>} Результат расчета
 */
export const calculateFinancialPlanSection = async (
  contextData,
  projectId,
  sectionId,
  sectionData,
) => {
  try {
    // Используем переданный контекст или получаем через API
    const context = contextData || {
      financialPlan: await getFinancialPlanData(projectId),
      sections: {},
    };
    const financialPlanData = context.financialPlan;
    const sectionsData = context.sections;

    // Обновляем данные секции в контексте, если переданы новые данные
    if (sectionData) {
      sectionsData[sectionId] = {
        ...sectionsData[sectionId],
        ...sectionData,
      };
    }

    // Формируем payload согласно новому API
    const payload = await buildFinancialPlanCalculationPayload(
      projectId,
      financialPlanData,
      sectionsData,
    );

    const response = await fetch(
      `${getAPIURL()}fin_plan/projects/${projectId}/calculate`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      let errorMessage = `HTTP error! status: ${response.status}`;
      try {
        const errorData = await response.json();
        errorMessage += ` - ${JSON.stringify(errorData)}`;
      } catch (e) {
        try {
          const errorText = await response.text();
          errorMessage += ` - ${errorText}`;
        } catch (e2) {
          // Игнорируем ошибку парсинга
        }
      }
      throw new Error(errorMessage);
    }

    const result = await response.json();

    // Обновляем контекст с результатами калькуляции
    const updatedContext = updateFinancialPlanContextWithCalculationResults(
      context,
      result,
    );

    return {
      ...result,
      updatedContext: updatedContext,
    };
  } catch (error) {
    console.error("Error calculating financial plan section:", error);
    throw error;
  }
};

/**
 * Обновляет контекст с результатами калькуляции финансового плана
 * @param {Object} context - Текущий контекст
 * @param {Object} calculationResults - Результаты калькуляции
 * @returns {Object} Обновленный контекст
 */
export const updateFinancialPlanContextWithCalculationResults = (
  context,
  calculationResults,
) => {
  if (!calculationResults) return context;

  // Обновляем данные финансового плана результатами калькуляции
  const updatedFinancialPlan = {
    ...context.financialPlan,
    ...calculationResults,
  };

  // Обновляем construction_design с новыми данными из construction_design_detailed
  if (calculationResults.construction_design_detailed) {
    const updatedConstructionDesign =
      updatedFinancialPlan.construction_design?.map((section) => {
        const detailedSection =
          calculationResults.construction_design_detailed.find(
            (det) => det.section_id === section.section.id,
          );

        if (detailedSection) {
          // Обновляем day_efforts на основе данных из block_expenditures
          const totalDirectEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.day_efforts || 0),
              0,
            ) || 0;
          const totalBonusEfforts =
            detailedSection.block_expenditures?.reduce(
              (sum, block) => sum + (block.bonus_day_efforts || 0),
              0,
            ) || 0;

          return {
            ...section,
            day_efforts: {
              direct: totalDirectEfforts,
              bonus: totalBonusEfforts,
              total: totalDirectEfforts + totalBonusEfforts,
            },
          };
        }

        return section;
      }) || [];

    updatedFinancialPlan.construction_design = updatedConstructionDesign;
  }

  // Обновляем детали секций с новыми данными из construction_design_detailed
  const updatedSections = { ...context.sections };

  if (calculationResults.construction_design_detailed) {
    calculationResults.construction_design_detailed.forEach(
      (detailedSection) => {
        if (updatedSections[detailedSection.section_id]) {
          updatedSections[detailedSection.section_id] = {
            ...updatedSections[detailedSection.section_id],
            block_expenditures: detailedSection.block_expenditures || [],
            bonus_percent: detailedSection.bonus_percent || 0,
          };
        }
      },
    );
  }

  return {
    financialPlan: updatedFinancialPlan,
    sections: updatedSections,
  };
};
